import { useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, X, Camera } from "lucide-react";
import { cn } from "@/lib/utils";
import { PhotoMomentBadge } from "./PhotoMomentBadge";
import type { PhotoMoment } from "@/lib/photoMoments";
import { isVideoMediaUrl } from "@/lib/mediaUrl";
import { Video } from "lucide-react";

function SubmissionMediaThumb({
  url,
  alt,
  className,
}: {
  url: string;
  alt: string;
  className?: string;
}) {
  if (isVideoMediaUrl(url)) {
    return (
      <div className={cn("relative h-full w-full bg-black", className)}>
        <video src={url} className="h-full w-full object-cover" muted playsInline preload="metadata" />
        <div className="absolute bottom-0.5 right-0.5 rounded bg-black/60 p-0.5">
          <Video className="h-2.5 w-2.5 text-white" />
        </div>
      </div>
    );
  }
  return <img src={url} alt={alt} className={className} loading="lazy" />;
}

function SubmissionMediaViewer({ url, alt, className }: { url: string; alt: string; className?: string }) {
  if (isVideoMediaUrl(url)) {
    return <video src={url} controls className={className} playsInline preload="metadata" />;
  }
  return <img src={url} alt={alt} className={className} loading="lazy" />;
}

interface Photo {
  id?: string;
  photo_url: string;
  room_name?: string;
  task_text?: string | null;
  type?: string; // "before" | "after" | "reference"
}

interface SubmissionPhotoGalleryProps {
  beforePhotos: Photo[];
  afterPhotos: Photo[];
  referencePhotos: Photo[];
  roomName?: string;
  taskText?: string;
  className?: string;
}

export const SubmissionPhotoGallery = ({
  beforePhotos,
  afterPhotos,
  referencePhotos,
  roomName,
  taskText,
  className,
}: SubmissionPhotoGalleryProps) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxPhotos, setLightboxPhotos] = useState<Photo[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lightboxType, setLightboxType] = useState<string>("");

  const openLightbox = useCallback((photos: Photo[], index: number, type: string) => {
    setLightboxPhotos(photos);
    setCurrentIndex(index);
    setLightboxType(type);
    setLightboxOpen(true);
  }, []);

  const navigateLightbox = useCallback((direction: "prev" | "next") => {
    if (direction === "prev") {
      setCurrentIndex((i) => (i > 0 ? i - 1 : lightboxPhotos.length - 1));
    } else {
      setCurrentIndex((i) => (i < lightboxPhotos.length - 1 ? i + 1 : 0));
    }
  }, [lightboxPhotos.length]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    (e.currentTarget as any)._touchStartX = e.touches[0].clientX;
    (e.currentTarget as any)._touchStartY = e.touches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    const startX = (e.currentTarget as any)._touchStartX;
    const startY = (e.currentTarget as any)._touchStartY;
    const diffX = e.changedTouches[0].clientX - startX;
    const diffY = e.changedTouches[0].clientY - startY;
    
    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
      diffX > 0 ? navigateLightbox("prev") : navigateLightbox("next");
    }
  }, [navigateLightbox]);

  const PhotoGrid = ({ 
    photos, 
    type, 
    label, 
    emptyText,
    moment,
  }: { 
    photos: Photo[]; 
    type: string; 
    label: string; 
    emptyText?: string;
    /** When supplied, render a moment badge so owners know which stage produced these photos. */
    moment?: PhotoMoment;
  }) => {
    if (photos.length === 0 && !emptyText) return null;

    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            {label} {photos.length > 0 && `(${photos.length})`}
          </p>
          {moment && photos.length > 0 ? <PhotoMomentBadge moment={moment} short /> : null}
        </div>
        {photos.length === 0 ? (
          <div className="flex items-center justify-center h-14 sm:h-16 rounded-lg border border-dashed bg-muted/30">
            <span className="text-xs text-muted-foreground">{emptyText}</span>
          </div>
        ) : (
          /* Horizontal scroll on mobile, auto-wrap on larger screens */
          <div className="flex gap-2 overflow-x-auto sm:overflow-x-visible sm:flex-wrap pb-1 sm:pb-0 -mx-1 px-1 scrollbar-thin snap-x snap-mandatory">
            {photos.map((photo, index) => (
              <button
                key={photo.id || `${type}-${index}`}
                onClick={() => openLightbox(photos, index, type)}
                className={cn(
                  "flex-shrink-0 snap-start group relative",
                  "active:scale-95 transition-transform"
                )}
              >
                <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-18 md:h-18 rounded-lg overflow-hidden border bg-muted">
                  <SubmissionMediaThumb
                    url={photo.photo_url}
                    alt={`${type} photo ${index + 1}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                  />
                </div>
                {photos.length > 1 && index === 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-medium">
                    {photos.length}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className={cn("space-y-3 sm:space-y-4", className)}>
        {(roomName || taskText) && (
          <div className="flex items-center gap-2 text-sm">
            <Camera className="w-4 h-4 text-muted-foreground shrink-0" />
            {roomName && <span className="font-medium">{roomName}</span>}
            {taskText && (
              <span className="text-muted-foreground truncate">– {taskText}</span>
            )}
          </div>
        )}
        
        {/* Stacked on mobile, 3-column grid on larger screens */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <PhotoGrid 
            photos={referencePhotos} 
            type="reference" 
            label="Reference" 
            emptyText="No reference"
          />
          <PhotoGrid 
            photos={beforePhotos} 
            type="before" 
            label="Before" 
            emptyText="No before"
            moment="cleaning_proof"
          />
          <PhotoGrid 
            photos={afterPhotos} 
            type="after" 
            label="After" 
            emptyText="No after"
            moment="cleaning_proof"
          />
        </div>
      </div>

      {/* Fullscreen Lightbox Dialog */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-[100vw] w-screen h-[100dvh] max-h-[100dvh] p-0 bg-black border-none rounded-none sm:rounded-lg sm:max-w-4xl sm:h-auto sm:max-h-[90vh] overflow-hidden">
          <DialogHeader className="sr-only">
            <DialogTitle>{lightboxType} Photo</DialogTitle>
          </DialogHeader>
          
          {lightboxPhotos.length > 0 && (
            <div 
              className="relative h-full"
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              {/* Close button - larger on mobile */}
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-4 right-4 z-10 w-12 h-12 sm:w-10 sm:h-10 text-white hover:bg-white/20 active:bg-white/30"
                onClick={() => setLightboxOpen(false)}
              >
                <X className="w-6 h-6 sm:w-5 sm:h-5" />
              </Button>

              {/* Image counter */}
              <div className="absolute top-4 left-4 z-10 px-3 py-1.5 rounded-full bg-black/50 text-white text-sm font-medium">
                {currentIndex + 1} / {lightboxPhotos.length} – {lightboxType}
              </div>

              {/* Main image container */}
              <div className="relative flex items-center justify-center min-h-[60vh] sm:min-h-[50vh] max-h-[calc(100dvh-100px)] sm:max-h-[80vh] touch-pan-y">
                <SubmissionMediaViewer
                  url={lightboxPhotos[currentIndex]?.photo_url ?? ""}
                  alt={`${lightboxType} photo ${currentIndex + 1}`}
                  className="max-w-full max-h-[75vh] sm:max-h-[80vh] object-contain px-2"
                />
              </div>

              {/* Navigation arrows - hidden on mobile */}
              {lightboxPhotos.length > 1 && (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="hidden sm:flex absolute left-2 top-1/2 -translate-y-1/2 text-white hover:bg-white/20"
                    onClick={() => navigateLightbox("prev")}
                  >
                    <ChevronLeft className="w-8 h-8" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="hidden sm:flex absolute right-2 top-1/2 -translate-y-1/2 text-white hover:bg-white/20"
                    onClick={() => navigateLightbox("next")}
                  >
                    <ChevronRight className="w-8 h-8" />
                  </Button>
                </>
              )}

              {/* Swipe hint on mobile */}
              {lightboxPhotos.length > 1 && (
                <div className="sm:hidden absolute bottom-20 left-1/2 -translate-x-1/2 text-white/60 text-xs">
                  Swipe to navigate
                </div>
              )}

              {/* Thumbnail strip */}
              {lightboxPhotos.length > 1 && (
                <div className="absolute bottom-0 left-0 right-0 flex gap-2 p-3 sm:p-4 overflow-x-auto bg-black/50 justify-start sm:justify-center scrollbar-thin">
                  {lightboxPhotos.map((photo, index) => (
                    <button
                      key={photo.id || index}
                      onClick={() => setCurrentIndex(index)}
                      className={cn(
                        "flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-lg overflow-hidden border-2 transition-all",
                        "active:scale-95",
                        index === currentIndex
                          ? "border-white opacity-100"
                          : "border-transparent opacity-60 hover:opacity-100"
                      )}
                    >
                      <SubmissionMediaThumb
                        url={photo.photo_url}
                        alt={`Thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

// Simple photo grid for single category display
interface SimplePhotoGridProps {
  photos: Photo[];
  onPhotoClick?: (photo: Photo) => void;
  className?: string;
}

export const SimplePhotoGrid = ({ photos, onPhotoClick, className }: SimplePhotoGridProps) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    (e.currentTarget as any)._touchStartX = e.touches[0].clientX;
    (e.currentTarget as any)._touchStartY = e.touches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    const startX = (e.currentTarget as any)._touchStartX;
    const startY = (e.currentTarget as any)._touchStartY;
    const diffX = e.changedTouches[0].clientX - startX;
    const diffY = e.changedTouches[0].clientY - startY;
    
    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
      if (diffX > 0) {
        setCurrentIndex((i) => (i > 0 ? i - 1 : photos.length - 1));
      } else {
        setCurrentIndex((i) => (i < photos.length - 1 ? i + 1 : 0));
      }
    }
  }, [photos.length]);

  if (photos.length === 0) return null;

  const handlePhotoClick = (index: number) => {
    if (onPhotoClick) {
      onPhotoClick(photos[index]);
    } else {
      setCurrentIndex(index);
      setLightboxOpen(true);
    }
  };

  return (
    <>
      {/* Horizontal scroll on mobile, wrap on larger screens */}
      <div className={cn(
        "flex gap-2 overflow-x-auto sm:overflow-x-visible sm:flex-wrap pb-1 sm:pb-0 -mx-1 px-1 scrollbar-thin snap-x snap-mandatory",
        className
      )}>
        {photos.map((photo, index) => (
          <button
            key={photo.id || index}
            onClick={() => handlePhotoClick(index)}
            className="flex-shrink-0 snap-start group relative active:scale-95 transition-transform"
          >
            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden border bg-muted">
              <SubmissionMediaThumb
                url={photo.photo_url}
                alt={`Photo ${index + 1}`}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform"
              />
            </div>
          </button>
        ))}
      </div>

      {/* Fullscreen Lightbox */}
      {!onPhotoClick && (
        <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
          <DialogContent className="max-w-[100vw] w-screen h-[100dvh] max-h-[100dvh] p-0 bg-black border-none rounded-none sm:rounded-lg sm:max-w-4xl sm:h-auto sm:max-h-[90vh] overflow-hidden">
            <DialogHeader className="sr-only">
              <DialogTitle>Photo View</DialogTitle>
            </DialogHeader>
            
            {photos.length > 0 && (
              <div 
                className="relative h-full"
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
              >
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-4 right-4 z-10 w-12 h-12 sm:w-10 sm:h-10 text-white hover:bg-white/20 active:bg-white/30"
                  onClick={() => setLightboxOpen(false)}
                >
                  <X className="w-6 h-6 sm:w-5 sm:h-5" />
                </Button>

                {photos.length > 1 && (
                  <div className="absolute top-4 left-4 z-10 px-3 py-1.5 rounded-full bg-black/50 text-white text-sm font-medium">
                    {currentIndex + 1} / {photos.length}
                  </div>
                )}

                <div className="flex items-center justify-center min-h-[60vh] sm:min-h-[50vh] max-h-[calc(100dvh-100px)] sm:max-h-[80vh] touch-pan-y">
                  <SubmissionMediaViewer
                    url={photos[currentIndex]?.photo_url ?? ""}
                    alt={`Photo ${currentIndex + 1}`}
                    className="max-w-full max-h-[75vh] sm:max-h-[80vh] object-contain px-2"
                  />
                </div>

                {photos.length > 1 && (
                  <>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="hidden sm:flex absolute left-2 top-1/2 -translate-y-1/2 text-white hover:bg-white/20"
                      onClick={() => setCurrentIndex((i) => (i > 0 ? i - 1 : photos.length - 1))}
                    >
                      <ChevronLeft className="w-8 h-8" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="hidden sm:flex absolute right-2 top-1/2 -translate-y-1/2 text-white hover:bg-white/20"
                      onClick={() => setCurrentIndex((i) => (i < photos.length - 1 ? i + 1 : 0))}
                    >
                      <ChevronRight className="w-8 h-8" />
                    </Button>
                  </>
                )}

                {/* Swipe hint */}
                {photos.length > 1 && (
                  <div className="sm:hidden absolute bottom-20 left-1/2 -translate-x-1/2 text-white/60 text-xs">
                    Swipe to navigate
                  </div>
                )}

                {photos.length > 1 && (
                  <div className="absolute bottom-0 left-0 right-0 flex gap-2 p-3 sm:p-4 overflow-x-auto bg-black/50 justify-start sm:justify-center scrollbar-thin">
                    {photos.map((photo, index) => (
                      <button
                        key={photo.id || index}
                        onClick={() => setCurrentIndex(index)}
                        className={cn(
                          "flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-lg overflow-hidden border-2 transition-all",
                          "active:scale-95",
                          index === currentIndex
                            ? "border-white opacity-100"
                            : "border-transparent opacity-60 hover:opacity-100"
                        )}
                      >
                        <SubmissionMediaThumb
                          url={photo.photo_url}
                          alt={`Thumbnail ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default SubmissionPhotoGallery;
